import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, ListItemIcon, ListItemText, MenuItem, Typography } from "@mui/material";
import type { GridAlignment, GridColDef, GridColumnMenuItemProps, GridColumnMenuProps } from "@mui/x-data-grid-premium";
import { GridColumnMenu } from "@mui/x-data-grid-premium";
import { useSelector } from "react-redux";
import { CalculationType } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ReportField } from "../../../../../../../../api/types/svTypes.ts";
import {
  selectCustomFields,
  selectFieldsForCalculation,
  selectTabularColumnAlignments,
} from "../../../../store/selectors/fieldsStateSelectors.ts";
import { isCustomColumn } from "../../../../utilities/fieldsHelper.ts";
import {
  applicableToMeasureColumnCalculationTypes,
  getExchangeableCalculationTypes,
} from "../../../builder/common/fields/custom-field/utilities/calculationTypeCapabilities.ts";
import useAvailableCalculationTypes from "../../../builder/common/hooks/useAvailableCalculationTypes.ts";
import ColumnMenuAlignmentItem from "./ColumnMenuAlignmentItem.tsx";
import ColumnMenuWidthItem from "./ColumnMenuWidthItem.tsx";
import ColumnMenuFreezeItem from "./freeze-item/ColumnMenuFreezeItem.tsx";
import SelectCalculationTypeMenuItem from "./SelectCalculationTypeMenuItem.tsx";

interface Props extends GridColumnMenuProps {
  onSetWidth: (column: GridColDef) => void;
  onSetAlignment: (column: GridColDef, align: GridAlignment | undefined) => void;
  onPredefinedCustomFieldType?: (
    calculationType: CalculationType,
    fieldGuid: string,
    anchorEl: HTMLElement | null
  ) => void;
}

const buildColumnMenu = (
  customField: ReportField | undefined,
  /** Whether the field that can be referenced in a custom formula */
  isFieldForCalculation: boolean,
  availableCalculationTypes: CalculationType[],
  onSetWidth: (column: GridColDef) => void,
  alignment: GridAlignment | undefined,
  onSetAlignment: (column: GridColDef, align: GridAlignment | undefined) => void,
  onPredefinedCustomFieldType?: (
    calculationType: CalculationType,
    fieldGuid: string,
    anchorEl: HTMLElement | null
  ) => void
) => {
  const slots: Record<string, React.ComponentType<GridColumnMenuItemProps> | null> = {};

  if (isCustomColumn(customField)) {
    if (customField.config.calculationType === CalculationType.CustomFormula) {
      slots["columnMenuEditCustomFieldItem"] = (p: GridColumnMenuItemProps) => (
        <MenuItem
          sx={{ gap: 1 }}
          onClick={(event) => {
            onPredefinedCustomFieldType?.(CalculationType.CustomFormula, customField.config.guid, null);
            p.onClick?.(event);
          }}
        >
          <>
            <ListItemIcon>
              <EditOutlinedIcon color="secondary" />
            </ListItemIcon>
            <ListItemText>
              <Typography>Edit Calculation</Typography>
            </ListItemText>
          </>
        </MenuItem>
      );
    } else if (
      customField.config.calculationType &&
      availableCalculationTypes.includes(customField.config.calculationType)
    ) {
      slots["columnMenuChangeCalculationTypeItem"] = (p: GridColumnMenuItemProps) => (
        <SelectCalculationTypeMenuItem
          onSetCustomField={onPredefinedCustomFieldType}
          {...p}
          availableCalculationTypeOptions={availableCalculationTypes}
        />
      );
    }
  } else if (isFieldForCalculation) {
    slots["columnMenuCalculationItem"] = (p: GridColumnMenuItemProps) => (
      <SelectCalculationTypeMenuItem
        onSetCustomField={onPredefinedCustomFieldType}
        {...p}
        availableCalculationTypeOptions={availableCalculationTypes}
      />
    );
  }

  slots["columnMenuPinLeftItem"] = ColumnMenuFreezeItem;
  slots["columnMenuWidthItem"] = (props: GridColumnMenuItemProps) => (
    <ColumnMenuWidthItem {...props} onSetWidth={onSetWidth} />
  );
  slots["columnMenuAlignmentItem"] = (props: GridColumnMenuItemProps) => (
    <ColumnMenuAlignmentItem {...props} alignment={alignment} onSetAlignment={onSetAlignment} />
  );
  slots["columnMenuAggregationItem"] = null;

  return slots;
};

const CustomColumnMenu = ({ onPredefinedCustomFieldType, onSetWidth, onSetAlignment, ...props }: Props) => {
  const columnAlignments = useSelector(selectTabularColumnAlignments);
  const customFields = useSelector(selectCustomFields);
  const fieldsForCalculation = useSelector(selectFieldsForCalculation);
  const availableCalculationTypes = useAvailableCalculationTypes();
  const customField = customFields.find((field) => field.config.guid === props.colDef?.field);
  const isFieldForCalculation = fieldsForCalculation.some((field) => field.config.guid === props.colDef?.field);

  const selectableCalculationTypes = isCustomColumn(customField)
    ? getExchangeableCalculationTypes(customField.config.calculationType).filter((type) =>
        availableCalculationTypes.includes(type)
      )
    : applicableToMeasureColumnCalculationTypes.filter((type) => availableCalculationTypes.includes(type));

  return (
    <Box sx={{ "& .MuiMenuItem-root .MuiListItemIcon-root": { minWidth: 0, mr: 1 } }}>
      <GridColumnMenu
        {...props}
        slots={buildColumnMenu(
          customField,
          isFieldForCalculation,
          selectableCalculationTypes,
          onSetWidth,
          columnAlignments.get(props.colDef.field),
          onSetAlignment,
          onPredefinedCustomFieldType
        )}
      />
    </Box>
  );
};

export default CustomColumnMenu;
